import React, { useState } from 'react';
import moment from "moment";
import { push as pushToUrl } from 'connected-react-router';
import { useGoogleLogin } from "@react-oauth/google";

import { useTheme } from '@material-ui/core/styles';
import { NavLink } from 'react-router-dom';
import {
  withWidth,
  useMediaQuery,
  Tooltip,
  AppBar,
  Box,
  Hidden,
  IconButton,
  Grid,
  Toolbar,
  Link,
  Typography
} from '@material-ui/core';

import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import CheckIcon from "@material-ui/icons/Check";
import MoreVertIcon from '@material-ui/icons/MoreVert';
import LanguageIcon from '@material-ui/icons/Language';
import { connectTo } from '../../utils/generic';
import * as globalActions from '../../actions/global';
import * as authActions from '../../actions/auth';
import * as trackingActions from '../../actions/tracking';
import * as facilitatorsConsentAgreementActions from "../../actions/facilitatorsConsentAgreement";

import { LANGUAGE_SELECTOR_CHANGE } from '../../constants/trackingEvents';
import { AuthorizeModal } from '../modals/authorize/authorizeModal';
import { FacilitatorsConsentModal } from  '../modals/members/consentModal';
import HqAvatar from '../avatar/avatar';
import UserDOBModal from '../modals/userProfile/userDOBModal';
import * as tokenStorage from '../../tokenStorage';
import { checkIsClubsDomain } from '../../utils/generic';
import { userIsAdult, userHasValidBirthday, userHasRoles, userIsAllowApplyOrAddChild } from "../../utils/userUtils";
import { takeIfExists } from "../../utils/local-storage";
import Roles from "../../utils/permissions";
import { CodeJamModal, CodeJamPickerModal, useCodeJamLauncher } from "../../modules/ide/codejam";
import BlockJamModal from "../modals/blockJamModal/blockJamModal";

import GWCLogo from '../../images/GWC_HQ_Logo.svg';
import GwcQaLogo from '../../images/gwcqalogo.png';
import CodeJamLogo from '../../images/CodeJam_logo.svg';

import * as colors from '../../theme/colors';
import CustomButton from '../customButton/customButton';
import { useNavBarStyles } from "./styles";
import { useCurrentPageMatch } from './hooks/useCurrentPageMatch';
import { useNavBackgroundColor } from './hooks/useNavBackgroundColor';
import { AppRoutes } from '../../constants';
import {
  TopMenuButton,
  StyledTopMenuItem,
  ResponsiveNestedMenu,
  StyledMenuItem,
  ResponsiveMenu,
} from './components';

const mainPath = '.girlswhocode.com';

const getCurrentLocale = (locales) => {
  if (!locales || locales.length === 0) {
    return;
  }
  const actualSubdomain = window.location.host.replace(mainPath, '');
  const filteredLocales = locales.filter(l => l.domain === actualSubdomain); // getting language by subdomain

  if (filteredLocales.length > 0) {
    return filteredLocales[0];
  }
}

const NavBar = (props) => {
  const {
    logout,
    authorizeParent,
    authorizeChild,
    auth: { currentRole, user, isUserLoading },
    languages,
    setHome,
    router,
    push,
    createTrackingLog,
    contentful,
    width,
    isMobile,
    setIsMobile,
    setFacilitatorsConsentAgreement,
    facilitatorsConsentAgreement: { isLoadingFacilitatorsConsentAgreement },
  } = props;

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [langAnchorEl, setLangAnchorEl] = React.useState(null);
  const [mobileEl, setMobileEl] = React.useState(null);
  const [children, setChildren] = React.useState(null);
  const [parents, setParents] = React.useState(null);
  const [passwordModal, setPasswordModal] = React.useState(false);
  const [selectedParent, setSelectedParent] = React.useState(null);
  const [navBackGroundColor, setNavBackGroundColor] = useNavBackgroundColor();
  const [showCalendar, setShowCalendar] = React.useState(false);
  const [labels, setLabels] = React.useState(null);

  const [mobileNavigation, setMobileNavigation] = React.useState(false);
  const [clubsWithoutFacilitatorConsent, setClubsWithoutFacilitatorConsent] = React.useState([]);
  const [openFacilitatorConsentModal, setOpenFacilitatorConsentModal] = React.useState(false);
  const [clubForConsent, setClubForConsent] = React.useState(null);
  const [userDOBModal, setUserDOBModal] = React.useState(false);
  const [addChildClicked, setAddChildClicked] = React.useState(false);
  const [blockJamModalOpened, setBlockJamModalOpened] = useState(false);

  const theme = useTheme();
  const classes = useNavBarStyles({navBackGroundColor});

  const _parentEmail = React.useRef(null); // useRef is used instead of useState because it's not visible inside google hook callback

  const open = Boolean(anchorEl);
  const openLang = Boolean(langAnchorEl);
  const openNavMobile = Boolean(mobileEl);

  const defaultlang = languages.find(e =>  e.isDefault === 'true');
  const hasAccountMenu = !!user && !!tokenStorage.getToken();
  const normalMode = useMediaQuery(theme.breakpoints.up('md'));
  const burgerMenuMode = useMediaQuery(theme.breakpoints.down('md'));
  const extraLargeScreen = useMediaQuery(theme.breakpoints.up('xl'));

  const logoWidth = extraLargeScreen ? 270 : 200;

  const { isCurrentPageMatch } = useCurrentPageMatch();

  const isClubsDomain = checkIsClubsDomain();
  const codeJam = useCodeJamLauncher(isClubsDomain);
  const tooltipText = "Skip to Content (Press Enter)";

  React.useEffect(() => {
    setIsMobile(!!isMobile);
  }, [isMobile]);

  React.useEffect(() => {
    window.scrollTo(0, 0);
    handleClose();

    setMobileNavigation(isMobile || !normalMode || burgerMenuMode);
  }, [router, width, normalMode, isMobile, burgerMenuMode, currentRole]);

  React.useEffect(() => {
    if (!contentful.hqConfig || !contentful.GlobalComponentQuery) {
      return;
    }

    const {
      hqConfig: { defaultProgram },
      GlobalComponentQuery: {
        header, membersPageLabels,
      }
    } = contentful;

    setLabels({ ...header, ...membersPageLabels });

    if(!user){
      return;
    }
    const { parents, children } = user;
    if (children) {
      setChildren(children);
    }

    if (parents) {
      setParents(parents)
    }

    const defaultProgramId = parseInt(defaultProgram);
    const isPartner = user.roles.filter((role) => role.roleId === 6);
    const userPrograms = user.roles
      .filter(
        (role) =>
          role.programId &&
          role.programId !== defaultProgramId &&
          !!role.program
      )
      .map((e) => e.programId);
    setShowCalendar(!!userPrograms.length || !!isPartner.length);
  }, [user, contentful]);

  React.useEffect(() => {
    if (!user) {
      return;
    }

    const facilitatorConsentStored = takeIfExists("facilitatorConsent", Object);
    const currentClubsWithoutFacilitatorConsent = user.roles.filter(role => {
      return role.program?.needsFacilitatorConsent && (!facilitatorConsentStored || !facilitatorConsentStored[role.programId]);
    }).map(({ program }) => program);
    setClubsWithoutFacilitatorConsent(currentClubsWithoutFacilitatorConsent);
  }, [user]);

  React.useEffect(() => {
    if (!clubsWithoutFacilitatorConsent.length) return;

    const clubForConsent = clubsWithoutFacilitatorConsent[0];
    setClubForConsent(clubForConsent);
    setOpenFacilitatorConsentModal(true);
  }, [clubsWithoutFacilitatorConsent, user]);

  React.useEffect(() => {
    setUserDOBModal(!userHasValidBirthday(user) && userHasRoles(user, [Roles.Viewer, Roles.CommunityPartner]));
  }, [user?.birthDate]);

  const handleMenu = event => {
    setNavBackGroundColor(colors.lightGradient);
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleLang = event => {
    setNavBackGroundColor(colors.lightGradient);
    setLangAnchorEl(langAnchorEl ? null : event.currentTarget);
  }

  const handleClose = () => {
    if (anchorEl) setAnchorEl(null);
    if (langAnchorEl) setLangAnchorEl(null);
    if (mobileEl) setMobileEl(null);
    setNavBackGroundColor(colors.white);
  };

  const onLogoClick = () => {
    setHome(true);
  }

  const onChildClick = (child) => {
    const { id: childId, roles, kinshipId, birthDate } = child;
    const isInvalidDOB = !birthDate || !moment(birthDate).isValid();

    if (!child || !kinshipId || isInvalidDOB) {
      return;
    }

    if (roles && roles.length > 0) {
      authorizeChild({childId, parentId: user.id});
    } else {
      push(`/join-program/${childId}`);
    }
  }

  const onParentClick = (parent) => {
    if (parent.passwordNotSet) {
      _parentEmail.current = parent.email;
      login();
      return;
    }

    setSelectedParent(parent);
    setPasswordModal(true);
  }

  const onCloseConsentModal = () => {
    let facilitatorConsent = takeIfExists("facilitatorConsent", Object);

    if (!facilitatorConsent) {
      localStorage.setItem("facilitatorConsent", JSON.stringify({
        [clubForConsent.id]: {
          ['closed']: {
            date: new Date(),
          }
        }
      }));
    } else {
      facilitatorConsent = {
        ...facilitatorConsent,
        [clubForConsent.id]: {
          ...facilitatorConsent[clubForConsent.id],
          ['closed']: {
            date: new Date(),
          }
        },
      }
      localStorage.setItem("facilitatorConsent", JSON.stringify(facilitatorConsent));
    }
    setClubsWithoutFacilitatorConsent(clubsWithoutFacilitatorConsent.filter(club => club.id !== clubForConsent.id));
    setClubForConsent(null);
  }

  const onCloseParentModal = () => {
    setSelectedParent(null);
    setPasswordModal(false);
  }

  const authorizeParentFunc = ({password}) => {
    const childToken = tokenStorage.getToken();

    authorizeParent({
      login: selectedParent.email,
      password,
      childId: user.id,
      childToken,
    });

    if (!isUserLoading) {
      setSelectedParent(null);
      setPasswordModal(false);
    }
  }

  const showRightNavigation = event => {
    setNavBackGroundColor(colors.lightGradient);
    setMobileEl(event.currentTarget);
  }

  const changeLocale = (lang) => {
    const newLocation = getLocation(lang);

    if (window.location != newLocation) {
      createTrackingLog({
        event: LANGUAGE_SELECTOR_CHANGE,
        data: JSON.stringify({
          from: window.location.href,
          to: newLocation
        })
      });
      window.location.href = newLocation;
    }
  }

  const getLocation = (lang) => {
    const delimiter = '/';
    let parts = window.location.href.split(delimiter);

    parts = parts.map(e => {
      const isDomain = e.indexOf(mainPath);
      if (isDomain !== -1) {
        e = `${lang.domain}${mainPath}`;
      }
      return e;
    });

    return `${parts.join('/')}`;
  }

  const currentLocale = getCurrentLocale(languages);

  const responseGoogle = (response) => {
    const { access_token } = response;

    authorizeParent({ login: _parentEmail.current, accessToken: access_token });
    _parentEmail.current = null;
    push(AppRoutes.HOME);
  };

  const responseGoogleError = () => {
    // TODO: next line was commented out because qa-automation gets alert for test.hq.girlswhocode.com
    // This should be handled properly
    // alert('Please, make sure cookies are enabled for current application');
  };

  const login = useGoogleLogin({
    onSuccess: (res) => {
      responseGoogle(res);
    },
    onError: () => responseGoogleError(),
  });

  const selectedLangCode = currentLocale ? currentLocale.code : defaultlang ? defaultlang.code : '';

  const renderLanguagesMenu = ({ open }) => (
    <ResponsiveNestedMenu
      id="languages"
      anchorEl={langAnchorEl}
      keepMounted
      open={open}
      onClose={handleClose}
      style={{ marginTop: 49 }}
      isMobile={mobileNavigation}
    >
      {languages && languages.map(el =>
        <StyledMenuItem key={el.id} onClick={() => { handleClose(); changeLocale(el); }}>
          {el.code === selectedLangCode && <CheckIcon className={classes.langCheckIcon} />}
          {el.code !== selectedLangCode && <div className={classes.langSpacer} />}
          {el.title}
        </StyledMenuItem>)}
    </ResponsiveNestedMenu>
  );

  const onAddChild = () => { handleClose(); props.push('/home?addchild=true') }

  const showDOBModal = () => { setUserDOBModal(true); setAddChildClicked(true); renderDOBModal() }

  const renderDOBModal = () => {
    return (
      <UserDOBModal
        onClose={() => {
          setUserDOBModal(false);
          setAddChildClicked(false);
        }}
        userDOBModal={userDOBModal}
        callback={() => userIsAdult(user) && onAddChild()}
        message='In order to add a child you must be at least 18 years old.'
      />
    );
  };

  const renderProfileMenu = ({ open }) => hasAccountMenu && (
    <ResponsiveNestedMenu
      id="profileMenu"
      anchorEl={anchorEl}
      keepMounted
      open={open}
      onClose={handleClose}
      isMobile={mobileNavigation}
      style={{ marginLeft: 16, marginTop: 49 }}
    >
      {children && children.map((child, index) => {
        return(<StyledMenuItem key={index} onClick={() => { handleClose(); onChildClick(child); }}>{child.firstName}</StyledMenuItem>);
      })}

      {parents && parents.map(e => {
        return(<StyledMenuItem onClick={() => { handleClose(); onParentClick(e); }}>{e.firstName}</StyledMenuItem>);
      })}
      {(userIsAdult(user) || userIsAllowApplyOrAddChild(user)) &&
      <StyledMenuItem onClick={() => userHasValidBirthday(user) ? onAddChild() : showDOBModal()}>Add Child</StyledMenuItem>}
      <StyledMenuItem onClick={() => { handleClose(); props.push(AppRoutes.USER_PROFILE) }} sel="view-profile">View Profile</StyledMenuItem>
      <StyledMenuItem onClick={() => { handleClose(); logout({logoutFromButton: true}) }} sel="logout">Logout</StyledMenuItem>
    </ResponsiveNestedMenu>
  );

  // no menu for BlockJam and TextJam pages
  if(isCurrentPageMatch(AppRoutes.BLOCK_JAM, { exact: false }) || isCurrentPageMatch(AppRoutes.TEXT_JAM, { exact: false })) {
    return null;
  }

  return (
    <div className={classes.root}>
      <AppBar
        position="fixed"
        className={classes.appBar}
      >
        <Toolbar className={classes.toolbar}>
          <Grid container>
            <Box
              width={isMobile || burgerMenuMode ? 0.95 : logoWidth}
              className={mobileNavigation ? classes.mobileLogoContainer : classes.logoContainer}>
              <Box component={NavLink} to={AppRoutes.HOME} onClick={() => onLogoClick()}>
                <img
                  alt="Girls Who Code HQ"
                  style={{ maxHeight: 65, fontSize: 0, width: 200 }}
                  src={window._env_.REACT_APP_PRODUCTION_LOGO ? GWCLogo : GwcQaLogo}
                />
              </Box>
              <Tooltip title={tooltipText}>
                <Link className={classes.mainContentLink} href="#maincontent">
                  <Typography variant="srOnly">{tooltipText}</Typography>
                </Link>
              </Tooltip>
            </Box>

            <Box width={isMobile || burgerMenuMode ? 0.05 : `calc(100% - ${logoWidth}px)`}>
              <Box className={classes.buttonsBox}>
              <Hidden only={mobileNavigation ? [] : ['md', 'lg', 'xl']}>
                <IconButton
                    color="inherit"
                    aria-label="Main menu"
                    aria-expanded="false"
                    edge="start"
                    onClick={showRightNavigation}
                  >
                  <MoreVertIcon />
                </IconButton>
                </Hidden>
                <ResponsiveMenu
                  id="def"
                  anchorEl={mobileEl}
                  keepMounted
                  open={openNavMobile}
                  isMobile={mobileNavigation}
                  onClose={handleClose}>
                    {!mobileNavigation && hasAccountMenu && [
                        <TopMenuButton
                          key="programs"
                          children="My Programs"
                          onClick={() => handleClose()}
                          className={classes.menuItemBox}
                          isCurrent={isCurrentPageMatch(AppRoutes.HOME)}
                          to={AppRoutes.HOME}
                          component={NavLink}
                          sel="programs"
                        />,
                        <TopMenuButton
                          key="calendar"
                          isRendered={showCalendar}
                          children="My Calendar"
                          onClick={() => handleClose()}
                          className={classes.menuItemBox}
                          isCurrent={isCurrentPageMatch(AppRoutes.CALENDAR)}
                          to={AppRoutes.CALENDAR}
                          component={NavLink}
                          sel="calendar"
                        />,
                        <TopMenuButton
                          key="my-projects"
                          children="My Projects"
                          onClick={() => handleClose()}
                          className={classes.menuItemBox}
                          isCurrent={isCurrentPageMatch(AppRoutes.MY_PROJECTS, { exact: false })}
                          to={AppRoutes.MY_PROJECTS}
                          component={NavLink}
                          sel="my-projects"
                        />,
                        /* Space between left and right parts of the menu */
                        <div key="spacer" style={{ marginRight: "auto" }} />,
                      ]}
                      {mobileNavigation && hasAccountMenu && [
                        <TopMenuButton
                          key="programs"
                          children="My Programs"
                          onClick={() => handleClose()}
                          to={AppRoutes.HOME}
                          component={NavLink}
                          sel="programs"
                        />,
                        <TopMenuButton
                          key="calendar"
                          isRendered={showCalendar}
                          children="My Calendar"
                          onClick={() => handleClose()}
                          to={AppRoutes.CALENDAR}
                          component={NavLink}
                          sel="calendar"
                        />,
                        <TopMenuButton
                          key="my-projects"
                          children="My Projects"
                          onClick={() => handleClose()}
                          className={mobileNavigation ? '' : classes.menuItemBox}
                          to={AppRoutes.MY_PROJECTS}
                          component={NavLink}
                          sel="my-projects"
                        />,
                        <TopMenuButton
                          key="code-jam"
                          children="CodeJam"
                          onClick={() => {
                            handleClose();
                            codeJam.openCodeJam();
                          }}
                          to="#"
                          component={NavLink}
                          sel="code-jam"
                        />,
                      ]}
                      {hasAccountMenu && !mobileNavigation &&
                        <Box
                          className={classes.codeJamLogo}
                          role="menuitem"
                          component={NavLink}
                          to="#"
                          onClick={() => {
                            codeJam.openCodeJam();
                          }}                        >
                          <img alt="CodeJam Logo" src={CodeJamLogo} />
                        </Box>
                      }
                      {(isClubsDomain && hasAccountMenu || !isClubsDomain) &&
                        <TopMenuButton
                          children="Project Gallery"
                          className={mobileNavigation ? '' : classes.menuItemBox}
                          onClick={() => handleClose()}
                          to={AppRoutes.PROJECT_GALLERY}
                          component={NavLink}
                          sel="project-gallery"
                          isCurrent={!mobileNavigation && isCurrentPageMatch(AppRoutes.PROJECT_GALLERY)}
                        />}
                        <TopMenuButton
                          className={mobileNavigation ? '' : classes.menuItemBox}
                          onClick={() => {
                            if (labels && labels.navBarHelpLink) {
                              window.open(labels.navBarHelpLink);
                            }
                          }}
                          sel="help"
                          children="Help"
                        />
                        <TopMenuButton
                          selected={openLang}
                          className={mobileNavigation ? '' : classes.menuItemBox}
                          onClick={handleLang}
                          sel="lang"
                          aria-controls="customized-menu-lang"
                          aria-label="Language selector"
                        >
                          <LanguageIcon className={classes.globeIcon} />
                          {selectedLangCode}
                          {openLang ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                        </TopMenuButton>
                      {renderLanguagesMenu({ open: openLang && mobileNavigation })}
                      {hasAccountMenu &&
                        <TopMenuButton
                          selected={open}
                          className={mobileNavigation ? '' : classes.menuItemBox}
                          onClick={handleMenu}
                          sel="profile"
                          aria-label="profile menu"
                          aria-controls="customized-menu"
                        >
                          <HqAvatar
                            user={user}
                            src={user.avatarName && user.avatarUrl}
                          />
                          {open ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                        </TopMenuButton>
                      }
                      {!hasAccountMenu && !isCurrentPageMatch(AppRoutes.LOGIN) && !isCurrentPageMatch(AppRoutes.LANDING) && (
                        <StyledTopMenuItem disableRipple onClick={() => { handleClose(); }} className={mobileNavigation ? '' : classes.menuItemBox}>
                          <CustomButton
                            mode="secondary"
                            className={classes.loginButton}
                            onClick={() => push('/')}
                          >Log in</CustomButton>
                        </StyledTopMenuItem>
                      )}
                      {renderProfileMenu({ open: open && mobileNavigation, user })}
                </ResponsiveMenu>
                {renderLanguagesMenu({ open: openLang && !mobileNavigation })}
                {renderProfileMenu({ open: open && !mobileNavigation, user })}
              </Box>
              <AuthorizeModal
                isOpen={passwordModal}
                onClose={onCloseParentModal}
                onSave={authorizeParentFunc}
                parent={selectedParent}/>
              {openFacilitatorConsentModal && clubForConsent && <FacilitatorsConsentModal
                onSubmit={() => {
                  setFacilitatorsConsentAgreement({ programId: clubForConsent.id });
                  onCloseConsentModal();
                }}
                onClose={() => {
                  onCloseConsentModal();
                }}
                labels={labels.consentAgreementModalText}
                programName={clubForConsent.name}
                isLoading={isLoadingFacilitatorsConsentAgreement}
                isOpen={openFacilitatorConsentModal}
              />}
              <CodeJamPickerModal
                onClose={codeJam.closePicker}
                onSelect={codeJam.selectIde}
                isOpen={codeJam.isPickerOpen}
              />
              <CodeJamModal
                onClose={codeJam.closeModal}
                onBack={isClubsDomain ? codeJam.backToPicker : undefined}
                ideType={codeJam.ideType}
                isOpen={codeJam.isModalOpen}
                push={push}
              />
              <BlockJamModal
                onClose={() => {
                  setBlockJamModalOpened(false);
                }}
                isOpen={blockJamModalOpened}
                push={push}
              />
            </Box>
          </Grid>
        </Toolbar>
      </AppBar>
      {user && userDOBModal && addChildClicked && renderDOBModal()}
    </div>
  );
}

export default connectTo(
  (state) => {
    return {
      auth: state.auth,
      languages: state.contentful.languages,
      contentful: state.contentful,
      router: state.router,
      facilitatorsConsentAgreement: state.facilitatorsConsentAgreement,
    }
  },
  {
    ...authActions,
    ...globalActions,
    ...trackingActions,
    ...facilitatorsConsentAgreementActions,
    push: pushToUrl,
  },
  withWidth()(NavBar),
);
